import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock,
  Download,
  Info,
  Plus,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Cabecera } from "@/components/propuesta/cabecera";
import { type FiltroElegido, Filtros } from "@/components/propuesta/filtros";
import { Estado } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/movimientos")({ component: Mesa });

type Fila = {
  id: string;
  fecha: string;
  importe: number;
  moneda: "PEN" | "USD";
  importePen: number | null;
  operacion: string | null;
  quePaso: string;
  detalle: string | null;
  banco: string;
  empresa: string | null;
  voucherPropuesto: string | null;
  voucherNota: string | null;
  regla: string | null;
  confianza: string | null;
  confianzaTono: "ok" | "warn" | "bad" | "neutro" | null;
  puntaje: number | null;
  estadoEtiqueta: string;
  estadoTono: "ok" | "warn" | "bad" | "loan" | "neutro";
  enLlano: string;
  motivo: string;
  motivoTono: "bad" | "warn" | "loan";
  boleta: string | null;
  boletaExtra: number;
};

type Grupo = {
  clave: string;
  banco: string;
  moneda: "PEN" | "USD";
  cuantos: number;
  suma: number;
};

type Bandeja =
  | "todos"
  | "por_decidir"
  | "conciliados"
  | "prestamos"
  | "sin_respaldo";

/** El título de la tarjeta, según la bandeja abierta. */
const TITULO: Record<Bandeja, string> = {
  todos: "Todos los abonos del periodo",
  por_decidir: "Movimientos que necesitan una decisión",
  conciliados: "Movimientos ya cruzados con su boleta",
  prestamos: "Movimientos entre cuentas del grupo",
  sin_respaldo: "Abonos que ningún voucher explica",
};

const PIE: Record<Bandeja, string> = {
  todos: "abonos",
  por_decidir: "por decidir",
  conciliados: "cruzados",
  prestamos: "entre empresas",
  sin_respaldo: "sin respaldo",
};

const ETIQUETA: Record<Bandeja, string> = {
  todos: "Todos",
  por_decidir: "Por decidir",
  conciliados: "Conciliados",
  prestamos: "Préstamos",
  sin_respaldo: "Sin respaldo",
};

// ===========================================================================
// La mesa de trabajo (lámina 2 de la propuesta).
//
// Nueve columnas, agrupada por banco y moneda, con selección múltiple y la
// barra de acciones anclada abajo — de SAP Fiori, vía la propuesta.
//
// El agrupado por banco NO es decorativo: los importes de dos bancos no se
// suman entre sí sin tipo de cambio, y trabajar una cuenta entera de una vez
// es como se concilia de verdad. Cada grupo dice cuántos movimientos tiene y
// cuánto suman, sobre TODO el filtro y no solo sobre la página.
// ===========================================================================

function Mesa() {
  const [bandeja, setBandeja] = useState<Bandeja>("por_decidir");
  const [buscar, setBuscar] = useState("");
  const [pagina, setPagina] = useState(0);
  // La misma lista, dos lecturas. Contabilidad quiere el número de operación y
  // la regla; quien solo tiene que decidir quiere saber qué pasó y por qué le
  // preguntan. Se recuerda, como en la portada.
  const [vista, setVista] = useState<"sencilla" | "completa">(
    () =>
      (localStorage.getItem("mesa.vista") as "sencilla" | "completa") ??
      "completa",
  );
  const sencilla = vista === "sencilla";
  const cambiarVista = (v: "sencilla" | "completa") => {
    setVista(v);
    localStorage.setItem("mesa.vista", v);
  };

  const qc = useQueryClient();
  const enLote = api.decidirVarias.useMutation();
  const prestamo = api.marcarPrestamo.useMutation();

  /**
   * Marcar lo seleccionado como movimiento entre empresas del grupo.
   *
   * Ecoplaza son 56 razones sociales que se transfieren dinero entre ellas, y
   * esos abonos no son el cobro de ninguna boleta. El motor todavía no los
   * detecta, así que lo dice una persona — y se puede deshacer desde la
   * bandeja «entre empresas».
   */
  const marcarPrestamo = (esPrestamo: boolean) => {
    const ids = [...marcados];
    if (ids.length === 0) return;
    prestamo.mutate(
      { body: { movimientoIds: ids, esPrestamo } },
      {
        onSuccess: (r: {
          status: number;
          body: { dice?: string; message?: string };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo marcar");
            return;
          }
          toast.success(r.body.dice ?? "Hecho");
          setMarcados(new Set());
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo marcar"),
      },
    );
  };

  /**
   * Decidir sobre todo lo marcado.
   *
   * Al terminar se quita la marca: dejar seleccionadas unas filas que acaban de
   * cambiar de estado invita a volver a pulsar el botón sobre lo mismo.
   */
  const decidirMarcados = (respuesta: "si" | "no" | "no_se") => {
    if (marcados.size === 0 || enLote.isPending) return;
    enLote.mutate(
      { body: { movimientoIds: [...marcados], respuesta } },
      {
        onSuccess: (r: {
          status: number;
          body: {
            dice?: string;
            saltadas?: number;
            motivos?: Array<{ motivo: string; cuantas: number }>;
          };
        }) => {
          if (r.status !== 200) {
            toast.error("No se pudo guardar la decisión");
            return;
          }
          // Lo que se quedó fuera se dice, no se calla: es la diferencia entre
          // un botón en el que se confía y uno que hay que ir a comprobar.
          toast.success(r.body.dice ?? "Hecho", {
            description: r.body.saltadas
              ? r.body.motivos
                  ?.map((m) => `${m.cuantas} ${m.motivo}`)
                  .join(" · ")
              : undefined,
          });
          setMarcados(new Set());
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo guardar la decisión"),
      },
    );
  };

  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [plegados, setPlegados] = useState<Set<string>>(new Set());
  const porPagina = 24;

  // Sobre qué dinero se está trabajando. La mesa solo sabía filtrar por
  // bandeja y por texto: no había forma de decir «solo el BCP en dólares»,
  // que es como se concilia de verdad — una cuenta entera de una vez.
  const [filtro, setFiltro] = useState<FiltroElegido>({});

  const { data, isPending } = api.mesa.useQuery(
    [
      "mesa",
      bandeja,
      buscar,
      String(pagina),
      filtro.empresaId ?? "",
      filtro.cuentaId ?? "",
      filtro.moneda ?? "",
    ],
    {
      query: {
        estado: bandeja,
        buscar: buscar || undefined,
        pagina,
        porPagina,
        agrupar: true,
        ...filtro,
      },
    },
  );

  const d = data?.status === 200 ? data.body : null;
  const filas = useMemo(() => (d?.filas ?? []) as Fila[], [d]);
  const grupos = (d?.grupos ?? []) as Grupo[];
  const paginas = d ? Math.ceil(d.total / porPagina) : 0;

  // Las filas de la página, repartidas en sus grupos.
  const porGrupo = useMemo(() => {
    const mapa = new Map<string, Fila[]>();
    for (const f of filas) {
      const clave = `${f.banco}|${f.moneda}`;
      const lista = mapa.get(clave);
      if (lista) lista.push(f);
      else mapa.set(clave, [f]);
    }
    return mapa;
  }, [filas]);

  const marcadosEnPagina = filas.filter((f) => marcados.has(f.id));
  const sumaMarcada = marcadosEnPagina.reduce((n, f) => n + f.importe, 0);
  const monedaMarcada = new Set(marcadosEnPagina.map((f) => f.moneda));
  const bancoMarcado = new Set(marcadosEnPagina.map((f) => f.banco));

  const alternar = (id: string) =>
    setMarcados((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const bandejas: Bandeja[] = [
    "todos",
    "conciliados",
    "por_decidir",
    "prestamos",
    "sin_respaldo",
  ];
  const cuenta = (b: Bandeja) =>
    d
      ? {
          todos: d.conteos.todos,
          por_decidir: d.conteos.porDecidir,
          conciliados: d.conteos.conciliados,
          prestamos: d.conteos.prestamos,
          sin_respaldo: d.conteos.sinRespaldo,
        }[b]
      : 0;

  return (
    <>
      <Cabecera
        migas={["Conciliación"]}
        titulo="Movimientos del extracto"
        buscar={buscar}
        alBuscar={(v) => {
          setBuscar(v);
          setPagina(0);
        }}
        marcador="Buscar un importe, una operación, una glosa…"
        facetas={[
          { clave: "Estado", valor: ETIQUETA[bandeja] },
          { clave: "Agrupar", valor: "Banco y moneda", tono: "gris" as const },
          ...(buscar
            ? [
                {
                  clave: "Texto",
                  valor: buscar,
                  tono: "gris" as const,
                  alQuitar: () => {
                    setBuscar("");
                    setPagina(0);
                  },
                },
              ]
            : []),
        ]}
        acciones={
          <>
            <div className="mr-1 flex rounded-md border p-0.5">
              {(["sencilla", "completa"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => cambiarVista(v)}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium text-[12px] transition-colors",
                    vista === v
                      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Vista {v}
                </button>
              ))}
            </div>
            {sencilla ? (
              <Button
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700"
                asChild
              >
                <Link to="/revision">
                  <ArrowRight className="mr-2 size-4" />
                  Revisar de uno en uno
                </Link>
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  disabled={marcados.size === 0 || enLote.isPending}
                  onClick={() => decidirMarcados("si")}
                >
                  <Check className="mr-2 size-4" />
                  Confirmar lo marcado
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href="/exportar/cierre.csv" download>
                    <Download className="mr-2 size-4" />
                    Exportar
                  </a>
                </Button>
              </>
            )}
          </>
        }
      />

      {/* Pestañas por bandeja. */}
      <div className="flex flex-wrap items-center gap-6 border-b">
        {bandejas.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => {
              setBandeja(b);
              setPagina(0);
            }}
            className={cn(
              "-mb-px flex items-center gap-2 border-b-2 px-1 pb-2.5 text-[13px] transition-colors",
              bandeja === b
                ? "border-cyan-500 font-semibold text-cyan-700 dark:text-cyan-400"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {ETIQUETA[b]}
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {cuenta(b).toLocaleString("es-PE")}
            </span>
          </button>
        ))}
      </div>

      {/* Los filtros. Van bajo las pestañas y no en la cabecera porque
          recortan LO QUE SE LISTA, igual que la bandeja. */}
      {d ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Filtros
            opciones={d.opciones}
            valor={filtro}
            alCambiar={(v) => {
              setFiltro(v);
              setPagina(0);
            }}
          />
          <span className="text-[11.5px] text-muted-foreground">
            {d.total.toLocaleString("es-PE")} {PIE[bandeja]}
          </span>
        </div>
      ) : null}

      {/* La banda que explica la regla del módulo. En la vista sencilla sobra:
          quien solo decide sí/no no necesita saber qué número viaja al asiento. */}
      {sencilla ? null : (
        <div className="flex items-start gap-2.5 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-[12.5px]">
          <Info className="mt-px size-4 shrink-0 text-sky-600 dark:text-sky-400" />
          <p>
            <b>Manda el número de operación del estado de cuenta.</b> Cuando el
            voucher trae otro distinto, el que viaja al reporte y al asiento es
            el del extracto; el del voucher se guarda como referencia.
          </p>
        </div>
      )}

      <Card className="pb-16">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="font-medium text-base">
            {TITULO[bandeja]}
          </CardTitle>
          <div className="flex items-center gap-3">
            {marcados.size > 0 ? (
              <span className="rounded bg-cyan-500/15 px-2 py-0.5 font-semibold text-[11px] text-cyan-700 uppercase tracking-wide dark:text-cyan-300">
                {marcados.size} marcados
              </span>
            ) : null}
            <span className="text-[11px] text-muted-foreground">
              ordenado por importe · mayor primero
            </span>
          </div>
        </CardHeader>

        <CardContent className="px-0">
          {isPending ? (
            <div className="space-y-2 px-6">
              {["a", "b", "c", "d", "e"].map((k) => (
                <Skeleton key={k} className="h-10" />
              ))}
            </div>
          ) : filas.length === 0 ? (
            <p className="px-6 py-8 text-center text-muted-foreground text-sm">
              No hay movimientos en esta bandeja.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="w-10 py-2 pl-6" />
                    <th className="py-2 pl-2 text-left font-medium">Fecha</th>
                    <th className="py-2 text-right font-medium">Importe</th>
                    {sencilla ? (
                      <>
                        <th className="py-2 pl-6 text-left font-medium">
                          Qué pasó
                        </th>
                        <th className="py-2 pl-6 text-left font-medium">
                          Boleta
                        </th>
                        <th className="py-2 pr-6 pl-6 text-left font-medium">
                          Por qué no se cerró solo
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="py-2 pl-6 text-left font-medium">
                          N.º operación
                        </th>
                        <th className="py-2 pl-6 text-left font-medium">
                          Descripción del banco
                        </th>
                        <th className="py-2 pl-6 text-left font-medium">
                          Voucher propuesto
                        </th>
                        <th className="py-2 pl-6 text-left font-medium">
                          Regla
                        </th>
                        <th className="py-2 pl-6 text-left font-medium">
                          Confianza
                        </th>
                        <th className="py-2 pr-6 pl-6 text-left font-medium">
                          Estado
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {grupos.map((g) => {
                    const suyas = porGrupo.get(g.clave) ?? [];
                    if (suyas.length === 0) return null;
                    const plegado = plegados.has(g.clave);
                    return (
                      <FragmentoGrupo
                        key={g.clave}
                        grupo={g}
                        filas={suyas}
                        plegado={plegado}
                        alPlegar={() =>
                          setPlegados((prev) => {
                            const s = new Set(prev);
                            if (s.has(g.clave)) s.delete(g.clave);
                            else s.add(g.clave);
                            return s;
                          })
                        }
                        marcados={marcados}
                        alMarcar={alternar}
                        sencilla={sencilla}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Barra de acciones anclada: aparece solo cuando hay algo marcado. */}
      {marcados.size > 0 ? (
        <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
          <p className="text-[12.5px]">
            <b>{marcados.size} movimientos marcados</b>
            <span className="text-muted-foreground">
              {" · "}
              {dinero(sumaMarcada, marcadosEnPagina[0]?.moneda ?? "PEN")}
              {monedaMarcada.size > 1 ? " · monedas distintas" : ""}
              {bancoMarcado.size === 1
                ? ` · todos de ${[...bancoMarcado][0]}`
                : ""}
            </span>
          </p>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMarcados(new Set())}
            >
              <X className="mr-2 size-4" />
              Quitar la marca
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={prestamo.isPending}
              onClick={() => marcarPrestamo(bandeja !== "prestamos")}
            >
              <Plus className="mr-2 size-4" />
              {bandeja === "prestamos"
                ? "Ya no es entre empresas"
                : "Marcar como préstamo"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={enLote.isPending}
              onClick={() => decidirMarcados("no_se")}
            >
              <Clock className="mr-2 size-4" />
              Dejar para después
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-red-500/40 text-red-600 hover:bg-red-500/10 dark:text-red-400"
              disabled={enLote.isPending}
              onClick={() => decidirMarcados("no")}
            >
              Rechazar la propuesta
            </Button>
            <Button
              size="sm"
              disabled={enLote.isPending}
              onClick={() => decidirMarcados("si")}
            >
              <Check className="mr-2 size-4" />
              Confirmar y conciliar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
          <p className="text-muted-foreground text-sm">
            {d ? (
              <>
                {/* El pie cuenta LA BANDEJA que se está mirando. Decir «1.779
                    por decidir» debajo de la lista de conciliados hace dudar de
                    qué está contando la pantalla. */}
                <span className="font-medium text-foreground">
                  {d.total.toLocaleString("es-PE")} {PIE[bandeja]}
                </span>
                {d.sumaPorDecidir.PEN > 0
                  ? ` · ${dinero(d.sumaPorDecidir.PEN, "PEN")}`
                  : ""}
                {d.sumaPorDecidir.USD > 0
                  ? ` · ${dinero(d.sumaPorDecidir.USD, "USD")}`
                  : ""}
                {paginas > 1 ? ` · página ${pagina + 1} de ${paginas}` : ""}
              </>
            ) : null}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina === 0}
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina + 1 >= paginas}
              onClick={() => setPagina((p) => p + 1)}
            >
              Siguiente
            </Button>
            <Button size="sm" variant="secondary" asChild>
              <Link to="/revision">
                <ArrowRight className="mr-2 size-4" />
                Revisar de uno en uno
              </Link>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function FragmentoGrupo({
  grupo,
  filas,
  plegado,
  alPlegar,
  marcados,
  alMarcar,
  sencilla,
}: {
  grupo: Grupo;
  filas: Fila[];
  plegado: boolean;
  alPlegar: () => void;
  marcados: Set<string>;
  alMarcar: (id: string) => void;
  sencilla: boolean;
}) {
  return (
    <>
      <tr className="border-b bg-muted/40">
        <td colSpan={sencilla ? 6 : 9} className="py-2 pl-6">
          <button
            type="button"
            onClick={alPlegar}
            className="flex items-center gap-2 text-left"
          >
            <ChevronDown
              className={cn(
                "size-3.5 transition-transform",
                plegado && "-rotate-90",
              )}
            />
            <b className="text-[12.5px]">
              {grupo.banco} · {grupo.moneda === "PEN" ? "Soles" : "Dólares"}
            </b>
            <span className="text-[11.5px] text-muted-foreground">
              {grupo.cuantos.toLocaleString("es-PE")} movimientos ·{" "}
              {dinero(grupo.suma, grupo.moneda)}
            </span>
          </button>
        </td>
      </tr>

      {plegado
        ? null
        : filas.map((f) => {
            const marcado = marcados.has(f.id);
            return (
              <tr
                key={f.id}
                className={cn(
                  "border-b transition-colors",
                  marcado ? "bg-cyan-500/10" : "hover:bg-muted/40",
                )}
              >
                <td className="py-2.5 pl-6">
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => alMarcar(f.id)}
                    aria-label={`Marcar el movimiento de ${dinero(f.importe, f.moneda)}`}
                    className="size-4 accent-cyan-600"
                  />
                </td>
                <td className="whitespace-nowrap py-2.5 tabular-nums">
                  {fecha(f.fecha)}
                </td>
                <td className="py-2.5 text-right font-semibold tabular-nums">
                  {dinero(f.importe, f.moneda)}
                  {f.importePen ? (
                    <div className="font-normal text-[11px] text-muted-foreground">
                      {dinero(f.importePen, "PEN")}
                    </div>
                  ) : null}
                </td>
                {sencilla ? (
                  <>
                    <td className="max-w-sm py-2.5 pl-6">
                      <Link
                        to="/movimiento/$id"
                        params={{ id: f.id }}
                        className="hover:underline"
                      >
                        {f.enLlano}
                      </Link>
                    </td>
                    <td className="py-2.5 pl-6 tabular-nums">
                      {f.boleta ? (
                        <>
                          {f.boleta}
                          {f.boletaExtra > 0 ? (
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              +{f.boletaExtra}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-6 pl-6">
                      <Estado tono={f.motivoTono}>{f.motivo}</Estado>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2.5 pl-6 text-muted-foreground tabular-nums">
                      {f.operacion ?? "—"}
                    </td>
                    <td className="max-w-xs py-2.5 pl-6">
                      <Link
                        to="/movimiento/$id"
                        params={{ id: f.id }}
                        className="block truncate hover:underline"
                        title={f.quePaso}
                      >
                        {f.quePaso}
                      </Link>
                      {f.detalle ? (
                        <div
                          className="truncate text-[11px] text-muted-foreground"
                          title={f.detalle}
                        >
                          {f.detalle}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2.5 pl-6">
                      {f.voucherPropuesto ? (
                        <>
                          <div>{f.voucherPropuesto}</div>
                          {f.voucherNota ? (
                            <div className="text-[11px] text-muted-foreground">
                              {f.voucherNota}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pl-6">
                      {f.regla ? (
                        <span className="rounded border px-1.5 py-0.5 font-mono text-[11px]">
                          {f.regla}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pl-6">
                      {f.confianza && f.confianzaTono ? (
                        <span className="flex items-center gap-1.5">
                          <Estado tono={f.confianzaTono}>{f.confianza}</Estado>
                          {/* El número detrás de la etiqueta. Dos cruces
                              pueden decir «alta» y no valer lo mismo: uno con
                              0,85 trae el final del número de operación, y
                              otro con 0,65 solo es el único que cuadraba. */}
                          {f.puntaje !== null ? (
                            <span
                              className="text-[11px] text-muted-foreground tabular-nums"
                              title="Cuánta evidencia hay, de 0 a 1"
                            >
                              {f.puntaje.toFixed(2)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-6 pl-6">
                      <Estado tono={f.estadoTono}>{f.estadoEtiqueta}</Estado>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
    </>
  );
}

function dinero(centimos: number, moneda: "PEN" | "USD"): string {
  const signo = moneda === "USD" ? "US$" : "S/";
  return `${signo} ${(centimos / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fecha(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}-${m}-${a}`;
}
